import type { PathConfig } from '@react-navigation/native';
import type { AppModalParams } from '../../../flows/Routes';
import type { OmitUnderscoreKeys } from '../../../utils/typeHelper';

const kStorefrontTabScreens = {
  home: { path: '' },
  orders: 'orders',
  inventory: 'inventory',
  clips: 'clips',
  commentchats: 'commentchats',
  myLife: 'my-life',
  myStore: 'my-store',
  mySubs: 'my-subs',
};

const kMyAccountScreen = {
  path: 'my-account',
  screens: {
    main: { path: '' },
  },
};

const kSaaSSubscriptionRedirectHandlerScreen = {
  path: 'saas-subscription-redirect-handler',
};

const kUserfrontTabScreens = {
  home: { path: '' },
  orders: 'orders',
  collections: 'collections',
};

export const screens: Record<
  OmitUnderscoreKeys<keyof AppModalParams>,
  string | PathConfig<any>
> = {
  messages: 'messages',

  home: {
    screens: {
      videoFeeds: '',
      discovery: 'discovery/:categoryUrlCode?',
      shop: 'shop/:categoryUrlCode?',
      user: {
        path: 'user',
        screens: {
          main: {
            path: '',
            screens: {
              ...kStorefrontTabScreens,
              ...kUserfrontTabScreens,
            },
          },
          myAccount: kMyAccountScreen,
          saasSubscriptionRedirectHandler:
            kSaaSSubscriptionRedirectHandlerScreen,
        },
      },
    },
  },

  storefront: {
    path: 'storefront',
    screens: {
      // main screen that is a tab navigator with several tab screens in it.
      main: {
        path: '',
        screens: kStorefrontTabScreens,
      },
      myAccount: kMyAccountScreen,
      saasSubscriptionRedirectHandler: kSaaSSubscriptionRedirectHandlerScreen,
    },
  },

  me: {
    path: 'me',
    screens: {
      main: {
        path: '',
        screens: kUserfrontTabScreens,
      },
      myAccount: {
        path: 'my-account',
        screens: {
          main: { path: '' },
        },
      },
    },
  },

  onboarding: 'login',
  termsOfService: 'terms-of-service',
  privacyPolicy: 'privacy-policy',
  setupCommentChat: 'commentchat/setup',
  startCommentChat: 'commentchat/start',
  createCommentChat: 'commentchat/create',
  editCommentChat: 'commentchat/edit',
  featuredEvents: 'featured-events',
  featuredStores: 'featured-stores',
  categoryDiscovery: 'discovery/category/:categoryId',
  discoveryEvent: 'discovery/events/:eventId',
  becomeASeller: 'become-a-seller',
  review: 'review/:id',
  popclip_v1: 'popclip_v1/:sellerId/:clipId',
  shopifyClips: 'shopifyClips/:sellerId/:clipId',
  csClips: 'csClips/:sellerId/:clipId',
  referralGifting: 'referralGifting/:id',
  userBadge: 'userBadge/:userBadgeId',
  storeListingDetail: 'products/:storeId/:listingId',
  debugPanel: 'debugPanel',
  slacklogin: 'slacklogin',
  scheduleShow: 'schedule',
  createPopClip: 'popclip/create',
  editPopClip: 'popclip/edit',
  deleteMyAccount: 'delete-my-account',
  shareplay: 'shareplay',

  goLive: 'goLive',

  search: {
    path: 'search',
    initialRouteName: 'accounts',
    exact: true,
    screens: {
      accounts: 'accounts',
      shows: 'shows',
      products: 'products',
    },
  },

  settings: {
    path: 'settings',
    screens: {
      abTest: 'abTest',
      followers: 'followers',
      followings: 'followings',
      ordersFlow: {
        path: 'orders',
        screens: {
          orderDetail: {
            path: ':orderId',
          },
          orderReview: ':orderId/review',
          orderRefundHistory: ':orderId/refunds',
          orderCancellationHistory: ':orderId/cancellations',
        },
      },
    },
  },

  ordersFlow: {
    path: '/orders',
    screens: {
      orderDetail: {
        path: '/orders/:orderId',
        exact: true,
      },
      orderReview: '/orders/:orderId/review',
      orderRefundHistory: '/orders/:orderId/refunds',
      orderCancellationHistory: '/orders/:orderId/cancellations',
    },
  },

  profile: {
    // classic profile page url (e.g., https://popshop.live/profile/xxxx)
    // referenced by user id, which is reserved for situations including:
    //  - backward compatibility purposes
    //  - for legacy sellers with `urlCode` unavailable
    //  - for legacy users with `username` unavailable
    path: 'profile/:uid',
    exact: true,
    initialRouteName: 'home',
    screens: {
      home: {
        path: '',
        initialRouteName: 'Main' as any,
        screens: {
          Main: { path: '' },
          Clips: 'clips',
          Shows: 'shows',
          Storefront: 'store',
        },
      },
      about: 'about',
    },
  },

  ////////////////////////////////////////////////////////////////////////
  // #################### MATCH ALL URL ROUTES CONFIG ####################
  ////////////////////////////////////////////////////////////////////////

  //#region match all url routes

  sellerProfile: {
    /**
     * urls (e.g., `https://popshop.live/3dretro`) refers to a seller profile
     * by url code.
     *
     * note(andyl): put this catch-all route before others so that now the profile
     * page by seller url code will be handling cases that looks like profile url
     * but are actually referring other types of pages.
     */
    path: ':sellerUrlCode',
    exact: true,
    initialRouteName: 'home',
    screens: {
      home: {
        path: '',
        initialRouteName: 'Main' as any,
        screens: {
          Main: { path: '' },
          Clips: 'clips',
          Shows: 'shows',
          Feeds: 'feed',
        },
      },
      about: 'about',
    },
  },

  userProfile: {
    /**
     * urls (e.g., `https://popshop.live/@andyl`) refers to profile by username.
     *
     * note(andyl): even though matching all urls but:
     *  - first, the username in profile page url will be prefixed by '@'
     *  - second, this kind of of url will be handled early when resolving urls
     */
    path: ':username',
    exact: true,
    initialRouteName: 'home',
    parse: {
      username: x => x.substring(1),
    },
    stringify: {
      username: x => `@${x}`,
    },
    screens: {
      home: {
        path: '',
        initialRouteName: 'Main' as any,
        screens: {
          Main: { path: '' },
          Reviews: 'reviews',
        },
      },
    },
  },

  showcase: {
    /**
     * urls (e.g., `https://popshop.live/liveShow_xxx`) refers to show cases
     * pages with the url code designated by some special prefixes, such as:
     * `liveShow_`, `upcomingShow_`, `event_`, `replay_`, etc.
     *
     * @see @file `src/functions/urlCodeHelpers.ts`
     *
     * note(andyl):
     *  - showcase was designed to handle additional special urls including:
     *      - seller url: https://popshop.live/sellerUrlCode
     *      - event url: https://popshop.live/eventUrlCode
     *    in the beginning. However, now this responsibility has been moved
     *    to `sellerProfile`.
     */
    path: ':urlCode',
  },

  //#endregion
};
